// @file Landside navigation store
import { trackPageview } from '@@/bits/analytics'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { landsideEventCategoryForPage } from '@@/bits/landside_analytics'
import { getPathnameItems } from '@@/bits/location'
import { getTaUrlWithUtmParams } from '@@/bits/ta'
import { ClassroomPlanPricingExperimentVariant, PricingPlansExperimentVariant } from '@@/enums'
import { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import type { User, WallGalleryTemplate } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum LandsidePage {
  Home = 'home',
  Product = 'product',
  Subscriptions = 'subscriptions',
  Templates = 'templates',
  Help = 'help',
  Careers = 'careers',
  WhatsNew = 'whatsnew',
  Pricing = 'pricing',
  DynamicLandingPage = 'dynamic_landing_page',
  Board = 'board',
  Sandbox = 'sandbox',
  Education = 'education',
  PadletArcade = 'PadletArcade',
}

/** The pages Maggi's router serves at their padlet.com URLs — the four under
 *  `/site` and the home page at the root. The router splits those paths per
 *  visitor (office allow-list first, then a percentage), so a page being listed
 *  here moves nobody to Maggi by itself: it only makes the click a real request
 *  the router can see. This is the only list of what has moved —
 *  `landsidePages` asks `migratedPageHrefMode` about its own key rather than
 *  restating it. */
const MIGRATED_PAGES: readonly LandsidePage[] = [
  LandsidePage.Home,
  LandsidePage.Board,
  LandsidePage.Sandbox,
  LandsidePage.Education,
  LandsidePage.Subscriptions,
]

/**
 * How a link to a landing page that has moved off Rails must navigate.
 *
 * Maggi's router owns these paths on padlet.com and picks Rails or Maggi per
 * visitor. A header click that only pushStates never leaves this app, so the
 * router never sees it. `Default` lets the anchor navigate.
 *
 * One flip (`landsideWorkerNavigation`) decides. There is no second
 * preview toggle. Off, every link behaves as it did before this change.
 *
 * Only pages in `MIGRATED_PAGES` leave the SPA. Every other page asks too and
 * is told to stay, so nothing outside that constant decides what has moved.
 */
function migratedPageHrefMode(page: LandsidePage): OzBaseButtonHrefMode {
  return MIGRATED_PAGES.includes(page) && isAppUsing('landsideWorkerNavigation')
    ? OzBaseButtonHrefMode.Default
    : OzBaseButtonHrefMode.HistoryPushState
}

/** An entry in the header, footer and side-panel navigation. */
interface LandsideNavigationPage {
  key: LandsidePage
  text: string
  href: string
  hrefMode: OzBaseButtonHrefMode
  iconName?: string
}

/** The same entry as written down, before the migration decides how it navigates. */
type LandsideNavigationPageDefinition = Omit<LandsideNavigationPage, 'hrefMode'> & {
  hrefMode?: OzBaseButtonHrefMode
}

/**
 * Whether a click on this page's link is fetching a new document rather than making an
 * in-app transition.
 *
 * `Default` mode does not stop a component's `@click` handler, so without this the SPA
 * transition still runs alongside the real navigation, and all of it is wrong:
 *
 * - it swaps in this app's component for a page the Worker is about to serve, showing
 *   the visitor the copy this migration exists to retire until the document lands;
 * - it emits a RudderStack pageview that the destination emits again on load
 *   (`shared/_pepin_analytics.html.erb` fires `rudderanalytics.page` on every Rails
 *   document, and Worker routes roll out independently of this flip), so migrated links
 *   double-count;
 * - for Subscriptions it also arms the `willReloadForSubscriptions` reload, which races
 *   the anchor's navigation and can land first, reloading the page the visitor just
 *   clicked away from.
 *
 * The click analytics live in the components (`trackEvent`, `trackLandsideEvent`) and
 * are untouched: what a full navigation drops is the SPA transition, not the click.
 */
function navigatesForReal(page: LandsidePage): boolean {
  return migratedPageHrefMode(page) === OzBaseButtonHrefMode.Default
}

interface PageChangeOptions {
  /**
   * Whether this page change is the browser handing back a URL it already had
   * (`popstate`) rather than a click leaving for a new document.
   *
   * `navigatesForReal` skips the SPA transition because the document is about to be
   * replaced. On Back there is no such navigation — this document is staying, and the
   * URL the browser just restored is now the only thing describing it. Skipping the
   * transition there leaves a migrated page's URL over the previous page's content, so
   * the guard has to stand down and let the app render what the URL says.
   */
  isRestoringHistory?: boolean
}

enum ProductPageTab {
  HOW_IT_WORKS = 'howitworks',
  EDUCATION = 'education',
}

enum TemplatesPageTab {
  GENERAL = 'general',
  EDUCATION = 'education',
  BUSINESS = 'business',
}

// Referenced from app/controllers/landside_controller.rb
const landsidePageTitles = {
  [LandsidePage.Home]: __('Creative tools for visual thinkers and learners'),
  [LandsidePage.Product]: __('Product'),
  [LandsidePage.Templates]: __('Templates'),
  [LandsidePage.Subscriptions]: __('Subscriptions'),
  [LandsidePage.Pricing]: __('Pricing'),
  [LandsidePage.DynamicLandingPage]: __('Creative tools for visual thinkers and learners'),
  [LandsidePage.Sandbox]: __('Sandbox'),
  [LandsidePage.Education]: __('Education'),
  [LandsidePage.Board]: __('Board'),
}

const productPageTabTitles = {
  [ProductPageTab.EDUCATION]: __('Padlet - Education'),
  [ProductPageTab.HOW_IT_WORKS]: __('Padlet - How it works'),
}

const templatesPageTabTitles = {
  [TemplatesPageTab.GENERAL]: __('Templates'),
  [TemplatesPageTab.EDUCATION]: __('Templates for education'),
  [TemplatesPageTab.BUSINESS]: __('Templates for business'),
}

const templatesPageTabDescriptions = {
  [TemplatesPageTab.GENERAL]: __(
    'Browse over 250 beautiful, free templates. Save time with easy-to-use boards designed for education, business, and beyond.',
  ),
  [TemplatesPageTab.EDUCATION]: __('Step-by-step guides for creating padlets for education.'),
  [TemplatesPageTab.BUSINESS]: __('Step-by-step guides for creating padlets for business.'),
}

/**
 * The landside page the current URL addresses, or `null` when the path is not a landside
 * route.
 *
 * The PostHog pageview properties read this rather than `currentPage`, because at the moment
 * they are read the store has not caught up yet: OzBaseButton pushes the new URL onto history
 * before it emits the click that updates the store, and PostHog captures its pageview inside
 * that pushState. The URL is therefore the only source that is already correct.
 *
 * The root is `Home`; an unrecognized path is `null` rather than `Home`. It used to be `Home`
 * too, which was right for the caller that renders the SPA — `/site/not-a-page` shows the
 * homepage — and is wrong now that `Home` is in `MIGRATED_PAGES`, because the pageview
 * properties would stamp `page: 'home'` at addresses no site serves as the home page. The
 * SPA caller states that fallback for itself instead.
 */
function landsidePageForPathname(): LandsidePage | null {
  // The first element in getPathnameItems for Landside returns 'site'
  const pathNameItems = getPathnameItems().filter((pathNameItem) => pathNameItem !== 'site')
  // `/` splits to [''], so an empty first segment is the root and nothing else.
  if ((pathNameItems[0] ?? '') === '') return LandsidePage.Home
  // If path represents a dynamic landing page, we set the landsidePage to DynamicLandingPage
  const landsidePage =
    pathNameItems[0] === 'product' && pathNameItems[1] === 'features'
      ? LandsidePage.DynamicLandingPage
      : (pathNameItems[0] as LandsidePage)
  return Object.values(LandsidePage).includes(landsidePage) ? landsidePage : null
}

const useLandsideNavigationStore = defineStore('landsideNavigation', () => {
  // State

  const currentPage = ref(LandsidePage.Home)
  const isUserLoggedIn = ref(false)
  const currentUser = ref<User | null>(null)
  const productPageTab = ref<ProductPageTab>(ProductPageTab.HOW_IT_WORKS)
  const pricingPlansExperimentVariant = ref<PricingPlansExperimentVariant>(PricingPlansExperimentVariant.Control)
  const classroomPlanPricingExperimentVariant = ref<ClassroomPlanPricingExperimentVariant>(
    ClassroomPlanPricingExperimentVariant.Control,
  )

  // Getters
  const landsidePages = computed((): LandsideNavigationPage[] => {
    // An entry names its own `hrefMode` only when the choice is about something
    // other than the migration: Home follows a signed-in visitor to /dashboard,
    // and Arcade is a different product in a new tab. Everything else is handed
    // its own key, so `MIGRATED_PAGES` stays the single answer to what has moved
    // and a page cannot be listed there yet keep pushStating here.
    //
    // Home names one and still asks. Signed in it must navigate whatever the flip
    // says, because Rails is what redirects to /dashboard; signed out it is a
    // migrated page like the rest. It has to ask inside its own entry, because the
    // `??` below only falls through on a missing `hrefMode` and Home always has one.
    const pages: LandsideNavigationPageDefinition[] = [
      {
        key: LandsidePage.Home,
        text: __('Home'),
        href: '/',
        hrefMode: isUserLoggedIn.value ? OzBaseButtonHrefMode.Default : migratedPageHrefMode(LandsidePage.Home),
      },
      {
        key: LandsidePage.Board,
        text: __('Board'),
        href: '/site/board',
      },
      {
        key: LandsidePage.Sandbox,
        text: __('Sandbox'),
        href: '/site/sandbox',
      },
      {
        key: LandsidePage.Subscriptions,
        text: __('Subscriptions'),
        href: '/site/subscriptions',
      },
      {
        key: LandsidePage.Templates,
        text: __('Templates'),
        href: '/site/templates',
      },
      {
        key: LandsidePage.Education,
        text: __('Education'),
        href: '/site/education',
      },
      {
        key: LandsidePage.PadletArcade,
        text: __('Arcade'),
        iconName: 'external_link',
        href: getTaUrlWithUtmParams('home', null, isUserLoggedIn.value),
        hrefMode: OzBaseButtonHrefMode.NewTab,
      },
    ]
    return pages.map((page) => ({ ...page, hrefMode: page.hrefMode ?? migratedPageHrefMode(page.key) }))
  })

  function initializeState(data: {
    isUserLoggedIn: boolean
    currentUser: User
    pricingPlansExperimentVariant: PricingPlansExperimentVariant
    classroomPlanPricingExperimentVariant: ClassroomPlanPricingExperimentVariant
  }): void {
    isUserLoggedIn.value = data.isUserLoggedIn ?? isUserLoggedIn.value
    currentUser.value = data.currentUser ?? currentUser.value
    pricingPlansExperimentVariant.value = data.pricingPlansExperimentVariant ?? pricingPlansExperimentVariant.value
    classroomPlanPricingExperimentVariant.value =
      data.classroomPlanPricingExperimentVariant ?? classroomPlanPricingExperimentVariant.value
  }

  function setCurrentPage(page: LandsidePage, { isRestoringHistory = false }: PageChangeOptions = {}): void {
    // If user is logged in, they should be directed to /dashboard and we want to prevent the UI from changing
    if (page === LandsidePage.Home && isUserLoggedIn.value) return
    if (page === LandsidePage.Help) return // Prevent the UI from changing since Help is an external url
    if (page === LandsidePage.Careers) return // Prevent the UI from changing since Careers is an external url
    if (page === LandsidePage.WhatsNew) return // Prevent the UI from changing since WhatsNew is an external url
    // The link is fetching its own document, so there is no transition to make here and
    // no pageview to emit — the destination emits its own. See navigatesForReal.
    if (!isRestoringHistory && navigatesForReal(page)) return

    const userFocusedElement = document.activeElement as HTMLElement
    const pricingSectionElement = document.querySelector('[data-testid="landside-subscription-pricing-section"')
    // Navigating to Subscriptions swaps to a full document reload unless the click came from
    // inside the pricing section. Read up front because the reload below tears this document
    // down, and `document.activeElement` only names the element the visitor clicked until then.
    const willReloadForSubscriptions =
      page === LandsidePage.Subscriptions && pricingSectionElement?.contains(userFocusedElement) === false

    currentPage.value = page
    // Separate landside funnel pageviews from the real homepage, which historically shared
    // the 'Homepage' category. The category comes from the same derivation the landside CTA
    // events use, so the five funnel pages get their 'Landside <Page>' category and
    // pageviews join clicks on category as well as the `page` property — and every other
    // page (home, product, pricing, dynamic landing pages) keeps 'Homepage', leaving
    // existing homepage dashboards untouched.
    trackPageview(landsideEventCategoryForPage(page), page.charAt(0).toUpperCase() + page.slice(1), { page })
    // No PostHog `$pageview` is captured here. The landside client captures one for itself on
    // every history change, so a second one here double-counts every SPA navigation — see
    // posthogPageviewProperties for how those automatic pageviews get their funnel properties.

    // if user tries to navigate to subscriptions page, it should only reload if user is clicking at the footer
    if (willReloadForSubscriptions) {
      // This reload used to be deferred so an in-flight PostHog capture was not aborted. It no
      // longer needs protecting: the pageview it was protecting is now captured by the client
      // during the pushState that precedes this, and the fresh document captures its own
      // pageview on init regardless. Waiting would only delay the navigation.
      //
      // Which does mean this one route reports two pageviews for one arrival: the pushState's
      // and the reloaded document's. It was three before this change, and it disappears
      // entirely once `landsideWorkerNavigation` is on, because the link then navigates for
      // real and `navigatesForReal` returns above before any of this runs.
      location.reload()
    }
  }

  function scrollToTop(): void {
    window.scrollTo({
      top: 0,
    })
  }

  function setCurrentPageWithSideEffects(
    page: LandsidePage,
    { isRestoringHistory = false }: PageChangeOptions = {},
  ): void {
    // Guarded here as well as in setCurrentPage, because these side effects are this
    // function's own: retitling and scrolling a document that is about to be replaced
    // shows the visitor the destination's title over the old page's content. See
    // navigatesForReal.
    if (!isRestoringHistory && navigatesForReal(page)) return

    setCurrentPage(page, { isRestoringHistory })

    // Side effects
    scrollToTop()

    // Update document title
    document.title = landsidePageTitles[page]
  }

  function setProductPageTab(tab: ProductPageTab): void {
    productPageTab.value = Object.values(ProductPageTab).includes(tab) ? tab : ProductPageTab.HOW_IT_WORKS
  }

  function setProductPageTabWithSideEffects(tab: ProductPageTab): void {
    setProductPageTab(tab)

    // Side effects
    scrollToTop()

    // Update document title
    document.title = productPageTabTitles[tab]
  }

  function updateTemplatePageTabMetadata(tab: TemplatesPageTab): void {
    // Update document title
    document.title = templatesPageTabTitles[tab]
    // Update document description
    document.querySelector('meta[name="description"]')?.setAttribute('content', templatesPageTabDescriptions[tab])
  }

  function updateTemplatePageMetadata(wallTemplate: WallGalleryTemplate): void {
    // Update document title
    document.title = __('%{wallTemplateTitle} - Padlet', { wallTemplateTitle: wallTemplate.title })
    // Update document description
    document.querySelector('meta[name="description"]')?.setAttribute('content', wallTemplate.description ?? '')
  }

  function setTemplatesPageTabWithSideEffects(tab: TemplatesPageTab): void {
    // Side effects
    scrollToTop()

    updateTemplatePageTabMetadata(tab)
  }

  /** Bound to `popstate` in LandsideApp.vue — see `PageChangeOptions.isRestoringHistory`
   *  for why these page changes are exempt from the real-navigation guard. */
  function switchPageToUrlParams(): void {
    // A path this app does not recognize renders the homepage, which is what it has always
    // done — `landsidePageForPathname` now says so here rather than by answering `Home`.
    const landsidePage = landsidePageForPathname() ?? LandsidePage.Home
    setCurrentPageWithSideEffects(landsidePage, { isRestoringHistory: true })

    if (landsidePage === LandsidePage.Product) {
      const pathNameItems = getPathnameItems().filter((pathNameItem) => pathNameItem !== 'site')
      setProductPageTab(pathNameItems[1] as ProductPageTab)
    }
  }

  /**
   * The funnel properties a PostHog pageview on a migrated page must carry, read at capture
   * time.
   *
   * CTA and section events get `page` and `category` from bits/landside_analytics. Pageviews
   * are captured by the PostHog client itself, so the client is handed this reader instead
   * (setLandsidePageviewProperties, wired up in pages/landside.ts). `loggedIn` matches the
   * property the CTA events already carry, so a conversion query can use a comparable
   * logged-out denominator on both sides of the funnel (MAR-815).
   *
   * Only the pages in `MIGRATED_PAGES` are stamped. These properties exist so a pageview on a
   * page the padlet.com router splits reads the same whichever site served it, and Maggi
   * serves no other page — stamping Templates or the product pages would mint `page` values
   * with no counterpart on the other side of that comparison, on pageviews whose dashboards
   * have always read them unlabeled. A page that moves starts being stamped by joining the
   * constant, the same way its links start navigating for real, and the home page is the
   * first to do it: Maggi publishes `content/pages/index.yml` at the root, so `page: 'home'`
   * now has a counterpart to be compared against.
   *
   * The home page keeps `category: 'Homepage'`, which is the category its pageviews have
   * always carried — the dedicated `Landside <Page>` categories are the five funnel pages'.
   * So this adds a `page` and a `loggedIn` to a homepage pageview and renames nothing.
   */
  function posthogPageviewProperties(): Record<string, unknown> {
    const page = landsidePageForPathname()
    if (page === null || !MIGRATED_PAGES.includes(page)) return {}
    return {
      category: landsideEventCategoryForPage(page),
      page,
      loggedIn: isUserLoggedIn.value,
    }
  }

  return {
    // State
    currentPage,
    productPageTab,
    isUserLoggedIn,
    currentUser,
    pricingPlansExperimentVariant,
    classroomPlanPricingExperimentVariant,

    // Getters
    landsidePages,

    // Actions
    initializeState,
    scrollToTop,
    setCurrentPage,
    setCurrentPageWithSideEffects,
    setProductPageTab,
    setProductPageTabWithSideEffects,
    setTemplatesPageTabWithSideEffects,
    updateTemplatePageTabMetadata,
    updateTemplatePageMetadata,
    switchPageToUrlParams,
    posthogPageviewProperties,
  }
})
export { LandsidePage, migratedPageHrefMode, ProductPageTab, TemplatesPageTab, useLandsideNavigationStore }
